/* eslint-disable no-param-reassign */
import {
    Euler,
    Raycaster,
    Matrix4,
    Quaternion,
    Vector2,
    Vector3,
    MathUtils,
} from 'three';
import EventEmitter from '../Utils/EventEmitter.js';
import Events from '../Utils/Events.js';
import PointerCoordinates from '../Utils/PointerCoordinates.js';
import Directions from './Directions.js';
import LocomotionArrowHelper from './LocomotionArrowHelper.js';

export default class SequentialWaypointControls {
    constructor(camera, domElement, time, debug, teleportOverlay) {
        // Setup
        this.camera = camera;
        this.domElement = domElement;
        this.time = time;
        this.pointer = new PointerCoordinates(domElement);
        this.locomotionArrows = new LocomotionArrowHelper();
        this.locomotionArrows.arrowHelper.visible = false;
        this.eventEmitter = new EventEmitter();
        // eslint-disable-next-line no-undef
        this.pictureFocusDiv = document.querySelector('.picture-focus');
        this.backButton = document.querySelector('button.back-button');
        this.detailButton = document.querySelector('button.detail-button');
        this.debug = debug;

        // Enable events
        this.isEnabled = true;
        this.isRotationEnabled = false;

        // Movement parameters
        this.speedRotating = 0.001;
        this.rotateAnimate = true;
        this.rotateDamping = 0.075;
        this.rotatePrecision = 0.1;
        this.cameraHeight = this.camera.position.y; // It is assumed that the camera is always at the same height
        this.speedKeyMoving = 2;
        this.moveParams = {
            moveTime: 1.6,
            lookAtRotationStep: 0.01,
            moveTimeFraction: 0.7,
        };

        // Movement helpers
        this.isRotating = false;
        this.movement = new Vector2();
        this.prevMovement = new Vector2();
        this.noMovement = new Vector2();
        this.prevScreen = new Vector2();
        this.raycaster = new Raycaster();
        this.targetRotationMatrix = new Matrix4();
        this.targetQuaternion = new Quaternion();
        this.isMoving = false;
        this.isAdjustingCameraAngle = false;
        this.moveDir = new Vector3();
        this.upVector = new Vector3(0, 1, 0);
        this.downVector = new Vector3(0, -1, 0);
        this.moveTarget = {
            targetPosition: new Vector3(),
            initialPosition: new Vector3(),
            targetDir: new Vector3(),
            targetDis: 0,
            targetAcc: 0,
            targetTime: 0,
            targetSpeed: 0,
            targetDisLater: 0,
            lookAtTime: 0,
            isItem: false,
        };

        // Waypoints and interactable objects
        this.waypoints = [];
        this.lastUsedWaypointIndex = 0;
        this.lastDirection = Directions.WAYPOINT_FORWARD;
        this.selectableItems = [];
        this.itemsIntersections = [];
        this.lastIntersectedItem = null;
        this.shouldLookAtItem = false;

        // Teleport
        this.fadeOverlay = teleportOverlay;
        this.isTeleporting = false;

        // Debug
        if (this.debug.active) {
            this.wasNoWaypointWarningShown = false;
            this.debugFolder = this.debug.ui.addFolder('Camera parameters');
            this.debugFolder
                .add(this.moveParams, 'moveTime')
                .name('Movement duration')
                .min(0.5)
                .max(5)
                .step(0.1);
            this.debugFolder
                .add(this.moveParams, 'lookAtRotationStep')
                .name('LookAt speed')
                .min(0.005)
                .max(0.5)
                .step(0.001);
            this.debugFolder
                .add(this.moveParams, 'moveTimeFraction')
                .name('Time after lookAt starts')
                .min(0.05)
                .max(1)
                .step(0.01);
            this.debugFolder
                .add(this.camera.position, 'y')
                .name('Camera height')
                .min(1.5)
                .max(4)
                .step(0.1);
            this.debugFolder
                .add(this.camera, 'fov')
                .name('Camera field of view')
                .min(35)
                .max(80)
                .step(1)
                .onChange(() => {
                    this.camera.updateProjectionMatrix();
                });
        }

        this.setupListeners();
    }

    setWaypoint(waypoint) {
        if (waypoint >= this.waypoints.length) {
            console.warn('The waypoint selected is out of range');
            return;
        }

        if (this.isTeleporting) return;

        // Save previous states and prevent interaction
        const wasEnabled = this.isEnabled;
        this.isEnabled = false;
        this.isMoving = false;
        this.isAdjustingCameraAngle = false;
        this.shouldLookAtItem = false;
        this.isTeleporting = true;
        this.pictureFocusDiv.style.display = 'none';
        this.locomotionArrows.arrowHelper.visible = false;
        this.resetMoveTarget();

        // Teleport to selected waypoint with fade in effect,
        // looking at the next waypoint if present, to the previous otherwise.
        this.fadeOverlay.showOverlay(() => {
            this.lastUsedWaypointIndex = waypoint;
            this.camera.position.set(
                this.waypoints[waypoint].position.x,
                this.cameraHeight,
                this.waypoints[waypoint].position.z
            );
            if (waypoint < this.waypoints.length - 1) {
                this.camera.lookAt(this.waypoints[waypoint + 1].position);
            } else {
                this.camera.lookAt(this.waypoints[waypoint - 1].position);
            }

            // Compute locomotion arrows state
            this.locomotionArrows.isForwardMovementPossible =
                this.lastUsedWaypointIndex < this.waypoints.length - 1;
            this.locomotionArrows.isBackwardMovementPossible =
                this.lastUsedWaypointIndex > 0;
            if (this.lastUsedWaypointIndex === this.waypoints.length - 1)
                this.locomotionArrows.shouldSwapArrows = true;

            // Restore previous states and then fade out the overlay
            this.isEnabled = wasEnabled;
            this.locomotionArrows.repositionArrows();
            this.fadeOverlay.hideOverlay(() => {
                this.isTeleporting = false;
                this.locomotionArrows.arrowHelper.visible = true;
            });
        });
    }

    resetMoveTarget() {
        this.moveTarget.targetPosition.set(0, 0, 0);
        this.moveTarget.initialPosition.set(0, 0, 0);
        this.moveTarget.targetDir.set(0, 0, 0);
        this.moveTarget.targetDis = 0;
        this.moveTarget.targetAcc = 0;
        this.moveTarget.targetTime = 0;
        this.moveTarget.targetSpeed = 0;
        this.moveTarget.targetDisLater = 0;
        this.moveTarget.lookAtTime = 0;
        this.moveTarget.isItem = false;
    }

    handleMovement(direction) {
        if (!Object.values(Directions).includes(direction)) {
            console.warn('The direction provided is not an instance of Directions');
            return;
        }

        // if (this.shouldLookAtItem) {
        //     this.isMoving = false;
        //     this.isAdjustingCameraAngle = false;
        //     return;
        // }

        this.isMoving = true;
        this.locomotionArrows.arrowHelper.visible = false;
        this.moveTarget.initialPosition.copy(this.camera.position);

        switch (direction) {
            case Directions.WAYPOINT_FORWARD:
                this.printNoWaypointsWarning();
                if (this.waypoints.length > 0) {
                    this.lastIntersectedItem = null;

                    // Check if the waypoint is the last one
                    const nextWaypointIndex = this.lastUsedWaypointIndex + 1;
                    this.locomotionArrows.isForwardMovementPossible =
                        nextWaypointIndex < this.waypoints.length - 1;
                    this.locomotionArrows.isBackwardMovementPossible =
                        nextWaypointIndex > 0;

                    // Compute movement
                    this.moveTarget.targetPosition.x =
                        this.waypoints[nextWaypointIndex].position.x;
                    this.moveTarget.targetPosition.y = this.cameraHeight;
                    this.moveTarget.targetPosition.z =
                        this.waypoints[nextWaypointIndex].position.z;

                    // Compute target rotation
                    const futureLookAtPoint =
                        nextWaypointIndex + 1 < this.waypoints.length
                            ? this.waypoints[nextWaypointIndex + 1].position
                            : this.waypoints[nextWaypointIndex - 1].position;
                    this.targetRotationMatrix.lookAt(
                        this.waypoints[nextWaypointIndex].position,
                        futureLookAtPoint,
                        this.upVector
                    );
                    this.targetQuaternion.setFromRotationMatrix(
                        this.targetRotationMatrix
                    );

                    if (nextWaypointIndex === this.waypoints.length - 1) {
                        this.locomotionArrows.rescaleBackwardArrow();
                        this.locomotionArrows.shouldSwapArrows = true;
                    }

                    if (
                        this.lastDirection !== direction &&
                        nextWaypointIndex !== this.waypoints.length - 1
                    ) {
                        this.locomotionArrows.rescaleForwardArrow();
                    }

                    this.lastUsedWaypointIndex = nextWaypointIndex;
                    this.moveTarget.targetDir
                        .subVectors(this.moveTarget.targetPosition, this.camera.position)
                        .normalize();
                    this.moveTarget.targetDis =
                        this.moveTarget.targetPosition.distanceToSquared(
                            this.camera.position
                        );
                    this.moveTarget.targetAcc =
                        (4 * Math.sqrt(this.moveTarget.targetDis)) /
                        this.moveParams.moveTime ** 2;
                    this.moveTarget.isItem = false;

                    this.isAdjustingCameraAngle = true;
                    this.lastDirection = direction;
                }

                break;
            case Directions.WAYPOINT_BACKWARD:
                this.printNoWaypointsWarning();
                if (this.waypoints.length > 0) {
                    this.lastIntersectedItem = null;

                    // Check if the waypoint is the first one
                    const previousWaypointIndex = this.lastUsedWaypointIndex - 1;
                    this.locomotionArrows.isForwardMovementPossible =
                        previousWaypointIndex < this.waypoints.length - 1;
                    this.locomotionArrows.isBackwardMovementPossible =
                        previousWaypointIndex > 0;

                    // Compute movement
                    this.moveTarget.targetPosition.x =
                        this.waypoints[previousWaypointIndex].position.x;
                    this.moveTarget.targetPosition.y = this.cameraHeight;
                    this.moveTarget.targetPosition.z =
                        this.waypoints[previousWaypointIndex].position.z;

                    // Compute target rotation
                    const futureLookAtPoint =
                        previousWaypointIndex - 1 >= 0
                            ? this.waypoints[previousWaypointIndex - 1].position
                            : this.waypoints[previousWaypointIndex + 1].position;
                    this.targetRotationMatrix.lookAt(
                        this.waypoints[previousWaypointIndex].position,
                        futureLookAtPoint,
                        this.upVector
                    );
                    this.targetQuaternion.setFromRotationMatrix(
                        this.targetRotationMatrix
                    );

                    // Adjust arrows direction if going backwards. Second and second from last waypoint are not involved
                    // because the camera makes a 180° turn in the last waypoint.
                    if (previousWaypointIndex !== 0) {
                        this.locomotionArrows.rescaleBackwardArrow();
                        this.locomotionArrows.shouldSwapArrows = true;
                    } else {
                        this.locomotionArrows.rescaleForwardArrow();
                    }

                    if (this.lastDirection !== direction && previousWaypointIndex !== 0) {
                        this.locomotionArrows.rescaleBackwardArrow();
                    }

                    this.lastUsedWaypointIndex = previousWaypointIndex;
                    this.moveTarget.targetDir
                        .subVectors(this.moveTarget.targetPosition, this.camera.position)
                        .normalize();
                    this.moveTarget.targetDis =
                        this.moveTarget.targetPosition.distanceToSquared(
                            this.camera.position
                        );
                    this.moveTarget.targetAcc =
                        (4 * Math.sqrt(this.moveTarget.targetDis)) /
                        this.moveParams.moveTime ** 2;
                    this.moveTarget.isItem = false;

                    this.isAdjustingCameraAngle = true;
                    this.lastDirection = direction;
                }

                break;
            case Directions.ITEM:
                this.printNoWaypointsWarning();

                if (this.selectableItems.length > 0) {
                    // Disable arrows when looking at an item
                    this.locomotionArrows.isForwardMovementPossible = false;
                    this.locomotionArrows.isBackwardMovementPossible = false;

                    // Need to avoid moving the camera to a surface (such as a wall) perpendicular to the y axis
                    // this.raycaster.set(this.camera.position, this.downVector)
                    this.moveTarget.targetPosition.x =
                        this.lastIntersectedItem.userData.observationPoint.x;
                    this.moveTarget.targetPosition.y =
                        this.lastIntersectedItem.userData.observationPoint.y; // + this.raycaster.intersectObjects(this.ground)[0].object.position.y
                    this.moveTarget.targetPosition.z =
                        this.lastIntersectedItem.userData.observationPoint.z;

                    // Compute target rotation
                    this.targetRotationMatrix.lookAt(
                        this.moveTarget.targetPosition,
                        this.lastIntersectedItem.userData.originalPosition,
                        this.upVector
                    );
                    this.targetQuaternion.setFromRotationMatrix(
                        this.targetRotationMatrix
                    );

                    if (this.moveTarget.targetPosition.equals(this.camera.position))
                        return;

                    this.moveTarget.targetDir
                        .subVectors(this.moveTarget.targetPosition, this.camera.position)
                        .normalize();
                    this.moveTarget.targetDis =
                        this.moveTarget.targetPosition.distanceToSquared(
                            this.camera.position
                        );
                    this.moveTarget.targetAcc =
                        (4 * Math.sqrt(this.moveTarget.targetDis)) /
                        this.moveParams.moveTime ** 2;
                    this.moveTarget.isItem = true;

                    this.isAdjustingCameraAngle = true;
                }

                break;
            case Directions.BACK:
                this.printNoWaypointsWarning();
                if (this.shouldLookAtItem) {
                    this.shouldLookAtItem = false;
                    if (this.waypoints.length > 0) {
                        this.lastIntersectedItem = null;

                        // Check if the waypoint is the last or first one
                        this.locomotionArrows.isForwardMovementPossible =
                            this.lastUsedWaypointIndex < this.waypoints.length - 1;
                        this.locomotionArrows.isBackwardMovementPossible =
                            this.lastUsedWaypointIndex > 0;

                        // Compute movement
                        this.moveTarget.targetPosition.x =
                            this.waypoints[this.lastUsedWaypointIndex].position.x;
                        this.moveTarget.targetPosition.y = this.cameraHeight;
                        this.moveTarget.targetPosition.z =
                            this.waypoints[this.lastUsedWaypointIndex].position.z;

                        // Compute target rotation
                        const futureLookAtPoint =
                            this.lastUsedWaypointIndex + 1 < this.waypoints.length
                                ? this.waypoints[this.lastUsedWaypointIndex + 1].position
                                : this.waypoints[this.lastUsedWaypointIndex - 1].position;
                        this.targetRotationMatrix.lookAt(
                            this.waypoints[this.lastUsedWaypointIndex].position,
                            futureLookAtPoint,
                            this.upVector
                        );
                        this.targetQuaternion.setFromRotationMatrix(
                            this.targetRotationMatrix
                        );

                        if (this.lastUsedWaypointIndex === this.waypoints.length - 1) {
                            this.locomotionArrows.rescaleBackwardArrow();
                            this.locomotionArrows.shouldSwapArrows = true;
                        } else {
                            this.locomotionArrows.rescaleForwardArrow();
                        }

                        this.moveTarget.targetDir
                            .subVectors(
                                this.moveTarget.targetPosition,
                                this.camera.position
                            )
                            .normalize();
                        this.moveTarget.targetDis =
                            this.moveTarget.targetPosition.distanceToSquared(
                                this.camera.position
                            );
                        this.moveTarget.targetAcc =
                            (4 * Math.sqrt(this.moveTarget.targetDis)) /
                            this.moveParams.moveTime ** 2;
                        this.moveTarget.isItem = false;

                        this.isAdjustingCameraAngle = true;
                    }
                }

                break;
            default:
        }
    }

    updateMovement(target, normalizedInterval) {
        if (this.shouldLookAtItem) return;
        this.isRotationEnabled = false;
        this.performUniformLinearMotion(target, normalizedInterval);
    }

    performUniformlyAcceleratedMotion(target, normalizedInterval) {
        target.targetTime = MathUtils.clamp(
            target.targetTime + normalizedInterval,
            0,
            this.moveParams.moveTime
        );

        const step = new Vector3();
        step.copy(this.camera.position);

        if (target.targetTime < this.moveParams.moveTime / 2) {
            // Increase speed during first half of the movement
            target.targetSpeed += target.targetAcc * normalizedInterval;

            // Uniformly accelerated motion equation
            step.addScaledVector(
                target.targetDir,
                target.targetSpeed * normalizedInterval +
                    0.5 * target.targetAcc * normalizedInterval * normalizedInterval
            );
        } else {
            // Decrease speed during second half of the movement
            target.targetSpeed -= target.targetAcc * normalizedInterval;

            // Uniformly accelerated motion equation
            step.addScaledVector(
                target.targetDir,
                target.targetSpeed * normalizedInterval -
                    0.5 * target.targetAcc * normalizedInterval * normalizedInterval
            );
        }

        target.targetDisLater = MathUtils.clamp(
            target.targetPosition.distanceToSquared(step),
            0,
            target.targetDis
        );

        // Camera movement
        if (target.targetDisLater >= target.targetDis) {
            // Target reached
            this.isMoving = false;
            this.camera.position.copy(step);
            if (target.isItem) this.camera.position.copy(target.targetPosition);
            target.targetDis = 0;
            target.targetTime = 0;
            target.targetSpeed = 0;

            if (this.lastIntersectedItem && !this.isAdjustingCameraAngle) {
                this.shouldLookAtItem = true;
                this.lastIntersectedItem.userData.setFocus();
            }
            if (!this.isAdjustingCameraAngle) {
                this.locomotionArrows.repositionArrows();
                this.locomotionArrows.arrowHelper.visible = true;
            }
        } else {
            // Target not reached yet
            this.camera.position.copy(step);
            target.targetDis = target.targetDisLater;
        }
    }

    performUniformLinearMotion(target, normalizedInterval) {
        target.targetTime = MathUtils.clamp(
            target.targetTime + normalizedInterval,
            0,
            this.moveParams.moveTime
        );
        const step = new Vector3();
        step.lerpVectors(
            target.initialPosition,
            target.targetPosition,
            target.targetTime / this.moveParams.moveTime
        );

        if (target.targetTime === this.moveParams.moveTime) {
            // Target reached
            this.isMoving = false;
            this.camera.position.copy(target.targetPosition);
            target.targetTime = 0;

            if (this.lastIntersectedItem && !this.isAdjustingCameraAngle) {
                this.shouldLookAtItem = true;
                this.lastIntersectedItem.userData.setFocus();
            }
            if (!this.isAdjustingCameraAngle) {
                this.locomotionArrows.repositionArrows();
                this.locomotionArrows.arrowHelper.visible = true;
            }
        } else {
            // Target not reached yet
            this.camera.position.copy(step);
        }
    }

    updateCameraRotation(target, interval) {
        if (this.shouldLookAtItem) return;

        this.isRotationEnabled = false;
        target.lookAtTime += interval;

        // Camera rotation
        if (
            target.lookAtTime >=
            this.moveParams.moveTime * this.moveParams.moveTimeFraction
        ) {
            if (
                1 - Math.abs(this.camera.quaternion.dot(this.targetQuaternion)) <
                0.0001
            ) {
                // Camera is looking at the target
                this.camera.quaternion.set(
                    this.targetQuaternion.x,
                    this.targetQuaternion.y,
                    this.targetQuaternion.z,
                    this.targetQuaternion.w
                );
                this.isAdjustingCameraAngle = false;
                target.lookAtTime = 0;
                if (this.lastIntersectedItem && !this.isMoving) {
                    this.shouldLookAtItem = true;
                    this.lastIntersectedItem.userData.setFocus();
                }
                if (!this.isMoving) {
                    this.locomotionArrows.repositionArrows();
                    this.locomotionArrows.arrowHelper.visible = true;
                }
            } else {
                // Camera is not looking at the target yet
                this.camera.quaternion.rotateTowards(
                    this.targetQuaternion,
                    this.moveParams.lookAtRotationStep
                );
            }
        }
    }

    update() {
        this.locomotionArrows.update();

        if (this.isRotating) {
            if (this.movement.equals(this.noMovement))
                this.movement.copy(this.prevMovement);

            // Compute camera rotation
            const tempEuler = new Euler(0, 0, 0, 'YXZ');
            tempEuler.setFromQuaternion(this.camera.quaternion);
            tempEuler.y += this.movement.x * this.speedRotating;
            tempEuler.x += this.movement.y * this.speedRotating;
            tempEuler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, tempEuler.x));

            this.camera.quaternion.setFromEuler(tempEuler);

            // Apply damping if enabled
            if (this.rotateAnimate) {
                this.movement.x *= 1 - this.rotateDamping;
                this.movement.y *= 1 - this.rotateDamping;
            } else {
                this.movement.copy(this.noMovement);
            }

            // Ignore small movements
            if (
                Math.abs(this.movement.x) <= this.rotatePrecision &&
                Math.abs(this.movement.y) <= this.rotatePrecision
            )
                this.isRotating = false;

            // Update rotation for next frame
            this.prevMovement.copy(this.movement);
            this.movement.copy(this.noMovement);
        }

        if (this.isMoving) {
            this.updateMovement(
                this.moveTarget,
                this.time.delta / 1000 / this.moveParams.moveTime
            );
        }

        if (this.isAdjustingCameraAngle) {
            this.updateCameraRotation(this.moveTarget, this.time.delta / 1000);
        }
    }

    setupListeners() {
        this.onContextMenu = (event) => {
            event.preventDefault();
        };

        this.onMouseDown = (event) => {
            if (!this.isEnabled) return;

            event.preventDefault();
            this.isRotationEnabled =
                !this.isMoving && !this.shouldLookAtItem && !this.isAdjustingCameraAngle;

            this.domElement.focus();

            if (event.touches) {
                this.prevScreen.x = event.touches[0].clientX;
                this.prevScreen.y = event.touches[0].clientY;
            }

            if (this.isRotationEnabled) {
                this.raycaster.setFromCamera(this.pointer.coordinates, this.camera);
                const intersects = this.raycaster.intersectObjects(this.selectableItems);
                if (intersects.length > 0) {
                    this.lastIntersectedItem = intersects[0].object;
                }
            }
        };

        this.onMouseMove = (event) => {
            if (!this.isEnabled) return;

            event.preventDefault();
            if (this.isRotationEnabled) {
                if (event.touches) {
                    // eslint-disable-next-line prefer-destructuring
                    event = event.touches[0];
                    event.movementX = (event.clientX - this.prevScreen.x) * 2;
                    event.movementY = (event.clientY - this.prevScreen.y) * 2;

                    this.prevScreen.x = event.clientX;
                    this.prevScreen.y = event.clientY;
                }

                this.movement.x +=
                    event.movementX || event.mozMovementX || event.webkitMovementX || 0;
                this.movement.y +=
                    event.movementY || event.mozMovementY || event.webkitMovementY || 0;

                this.isRotating = true;
            }
        };

        this.onMouseUp = (event) => {
            if (!this.isEnabled) return;
            if (!this.isRotationEnabled) return;

            event.preventDefault();
            this.isRotationEnabled = false;
            this.raycaster.setFromCamera(this.pointer.coordinates, this.camera);
            const intersects = this.raycaster.intersectObjects(this.selectableItems);
            if (this.lastIntersectedItem) {
                // this.lastIntersectedItem.material.emissive.set(
                //     this.lastIntersectedItem.userData.originalColor
                // );
                // gsap.to(this.lastIntersectedItem.material.emissive, {
                //     duration: 0.5,
                //     r: this.lastIntersectedItem.userData.originalColor,
                // });
                if (
                    intersects.length > 0 &&
                    intersects[0].object === this.lastIntersectedItem
                ) {
                    // this.lastIntersectedItem.userData.setFocus(event);
                    this.eventEmitter.trigger(Events.TRIGGER_MOVEMENT, [Directions.ITEM]);
                }
            }
        };

        this.onBackFromItem = () => {
            if (this.shouldLookAtItem) {
                this.shouldLookAtItem = false;
                this.pictureFocusDiv.style.display = 'none';
                this.eventEmitter.trigger(Events.TRIGGER_MOVEMENT, [Directions.BACK]);
            }
        };

        this.domElement.addEventListener('contextmenu', this.onContextMenu, false);
        this.domElement.addEventListener('mousedown', this.onMouseDown, false);
        this.domElement.addEventListener('mousemove', this.onMouseMove, false);
        this.domElement.addEventListener('mouseup', this.onMouseUp, false);
        this.domElement.addEventListener('touchstart', this.onMouseDown, false);
        this.domElement.addEventListener('touchmove', this.onMouseMove, false);
        this.domElement.addEventListener('touchend', this.onMouseUp, false);

        this.eventEmitter.on(Events.TRIGGER_MOVEMENT, this, (direction) =>
            this.handleMovement(direction)
        );
        this.eventEmitter.on(Events.TELEPORT, this, (roomWaypointIndex) => {
            this.setWaypoint(roomWaypointIndex);
        });
    }

    destroy() {
        this.domElement.removeEventListener('contextmenu', this.onContextMenu, false);
        this.domElement.removeEventListener('mousedown', this.onMouseDown, false);
        this.domElement.removeEventListener('mousemove', this.onMouseMove, false);
        this.domElement.removeEventListener('mouseup', this.onMouseUp, false);
        this.domElement.removeEventListener('touchstart', this.onMouseDown, false);
        this.domElement.removeEventListener('touchmove', this.onMouseMove, false);
        this.domElement.removeEventListener('touchend', this.onMouseUp, false);

        this.eventEmitter.off(Events.TRIGGER_MOVEMENT, this);
        this.eventEmitter.off(Events.TELEPORT, this);
        this.locomotionArrows.destroy();
    }

    printNoWaypointsWarning() {
        if (this.debug.active && this.waypoints.length === 0) {
            console.warn(
                'No waypoints found. Please add at least one waypoint to the scene.'
            );
        }
    }
}
